import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { AlertCircle, CheckCircle, FileText, Shield, AlertTriangle, Clock, Zap, Download, TrendingUp, Bot, BookOpen } from 'lucide-react';
import OnboardingWizard from './components/OnboardingWizard';
import * as apiService from './services/apiService';
import * as awsApi from './services/awsApiService';
import { demoAutomationActivity, demoAutomationStream } from './data/demoData';
import { useAuth, useDemoTour } from './contexts';
import * as reportService from './services/reportGenerationService';

// Module-level state — persists across Dashboard remounts during the same tour
let _automationFiredThisTour = false;
let _persistedStreamItems = [];
let _persistedAutomationComplete = false;

const SkeletonChart = ({ height = 'h-48' }) => (
  <div className={`bg-white rounded-xl shadow-sm p-6 border border-slate-200 animate-pulse`}>
    <div className="h-4 bg-slate-200 rounded w-1/4 mb-4"></div>
    <div className={`${height} bg-slate-100 rounded-lg flex items-center justify-center`}>
      <div className="w-8 h-8 border-2 border-teal-600 border-t-transparent rounded-full animate-spin"></div>
    </div>
  </div>
);

const App = () => {
  const { practice } = useAuth();
  const { currentStep, isActive: tourIsActive } = useDemoTour() || {};
  const location = useLocation();
  const navigate = useNavigate();
  const appMode = apiService.getAppMode();

  const [activeTab, setActiveTab] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    const tab = params.get('tab');
    return tab === 'reports' ? 'reports' : 'dashboard';
  });
  const [deniedClaims, setDeniedClaims] = useState([]);
  const [dashboardLoading, setDashboardLoading] = useState(false);
  const [dataLastFetched, setDataLastFetched] = useState(null);
  const [reportLoading, setReportLoading] = useState(null);
  const [reportMessage, setReportMessage] = useState(null);
  const [agingData, setAgingData] = useState(null);
  const [automationActivity, setAutomationActivity] = useState(
    appMode === 'demo' ? demoAutomationActivity : {
      todaySummary: { auto_appeal_generated: 0, claim_aging_flagged: 0, claim_scrubbed: 0, payment_reminder_sent: 0 },
      recentActivity: []
    }
  );
  const [showOnboarding, setShowOnboarding] = useState(() => {
    return new URLSearchParams(window.location.search).get('setup') === '1';
  });
  const [showActivityLog, setShowActivityLog] = useState(false);
  const [allActivity, setAllActivity] = useState([]);
  const [activityPage, setActivityPage] = useState(1);
  const [activityTotal, setActivityTotal] = useState(0);
  const [activityLoading, setActivityLoading] = useState(false);
  const [runningAutomation, setRunningAutomation] = useState(false);
  const [automationComplete, setAutomationComplete] = useState(_persistedAutomationComplete);
  const [streamItems, setStreamItems] = useState(_persistedStreamItems);

  // Sync active tab and setup param from URL search params
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tab = params.get('tab');
    if (tab === 'reports') setActiveTab('reports');
    else if (!tab && location.pathname === '/') setActiveTab('dashboard');
    if (params.get('setup') === '1') setShowOnboarding(true);
    else setShowOnboarding(false);
  }, [location.search]);

  // Check if onboarding is needed (skip in demo mode)
  useEffect(() => {
    if (!practice?.practice_id) return;
    if (window.location.hostname === 'demo.flowrcmdental.com') return;
    awsApi.getPractice().then(res => {
      if (res.success && res.data && res.data.setup_complete === false) {
        setShowOnboarding(true);
      }
    }).catch(() => {});
  }, [practice]);

  // Load real metrics from API when practice is available (works in both demo and production)
  useEffect(() => {
    const loadRealMetrics = async (isRefresh = false) => {
      // Skip if data was fetched recently (within 30 seconds) and not a forced refresh
      if (!isRefresh && dataLastFetched && Date.now() - dataLastFetched < 30000) {
        setDashboardLoading(false);
        return;
      }

      if (appMode === 'demo') {
        // Demo mode — always use pre-built demo data regardless of practice.id
        setAutomationActivity(demoAutomationActivity);
        setDashboardLoading(false);
      } else if (practice?.id) {
        try {
          const claimsResult = await awsApi.getClaims(practice.id, { status: 'denied', limit: 20 });
          if (claimsResult.success) setDeniedClaims(claimsResult.data || []);

          const [agingResult, activityResult] = await Promise.all([
            awsApi.getClaimAging(),
            awsApi.getAutomationActivity(),
          ]);
          if (agingResult.success) setAgingData(agingResult.data);
          if (activityResult.success) {
            setAutomationActivity(prev => {
              if (!prev) return activityResult.data;
              const prevSummary = prev?.todaySummary || {};
              const freshSummary = activityResult.data?.todaySummary || {};
              const allKeys = new Set([...Object.keys(prevSummary), ...Object.keys(freshSummary)]);
              const merged = {};
              allKeys.forEach(k => { merged[k] = Math.max(prevSummary[k] || 0, freshSummary[k] || 0); });
              return { ...activityResult.data, todaySummary: merged };
            });
          }

          setDataLastFetched(Date.now());
        } catch (error) {
          console.error('[Dashboard] Error loading real metrics:', error);
        } finally {
          setDashboardLoading(false);
        }
      } else {
        setDashboardLoading(false);
      }
    };

    loadRealMetrics();
    // Refresh every 30 seconds
    const refreshInterval = practice?.id ? setInterval(() => loadRealMetrics(true), 30000) : null;

    return () => {
      if (refreshInterval) clearInterval(refreshInterval);
    };
  }, [practice?.id]);


  const handleViewAllActivity = async (page = 1) => {
    setShowActivityLog(true);
    if (appMode === 'demo') {
      setAllActivity(demoAutomationActivity.activity || []);
      setActivityTotal(demoAutomationActivity.activity?.length || 0);
      setActivityPage(1);
      return;
    }
    setActivityLoading(true);
    const result = await awsApi.getAllAutomationActivity(page, 25);
    if (result.success) {
      setAllActivity(result.data.activity || []);
      setActivityTotal(result.data.pagination?.total || 0);
      setActivityPage(page);
    }
    setActivityLoading(false);
  };

  const handleReportDownload = async (reportType) => {
    setReportLoading(reportType);
    setReportMessage(null);

    try {
      let result;
      const practiceId = 'demo-practice-id';

      switch(reportType) {
        case 'AR Aging Detail':
          result = await reportService.generateARAgingReport(practiceId, appMode);
          break;
        case 'Denial Analysis':
          result = await reportService.generateDenialAnalysisReport(practiceId, appMode);
          break;
        case 'Payer Performance Summary':
          result = await reportService.generatePayerPerformanceReport(practiceId, appMode);
          break;
        case 'Production Report':
          result = await reportService.generateProductionReport(practiceId, appMode);
          break;
        case 'Collection Report':
          result = await reportService.generateCollectionReport(practiceId, appMode);
          break;
        case 'Workflow Activity':
          result = await reportService.generateWorkflowActivityReport(practiceId, appMode);
          break;
        default:
          throw new Error('Unknown report type');
      }

      if (result.success) {
        setReportMessage({ type: 'success', text: 'Report downloaded successfully!' });
      } else {
        setReportMessage({ type: 'error', text: result.error || 'Failed to generate report' });
      }
    } catch (error) {
      setReportMessage({ type: 'error', text: error.message });
    } finally {
      setReportLoading(null);
      setTimeout(() => setReportMessage(null), 5000);
    }
  };

  // Persist stream items and complete state across remounts
  useEffect(() => { _persistedStreamItems = streamItems; }, [streamItems]);
  useEffect(() => {
    _persistedAutomationComplete = automationComplete;
    if (!automationComplete) { _persistedStreamItems = []; } // reset on tour end
  }, [automationComplete]);

  // Reset animation state when switching accounts/practices
  useEffect(() => {
    _persistedStreamItems = [];
    _persistedAutomationComplete = false;
    _automationFiredThisTour = false;
    setStreamItems([]);
    setAutomationComplete(false);
  }, [practice?.practice_id]); // eslint-disable-line

  // Auto-trigger Claim Robot stream when demo tour hits the dashboard steps
  useEffect(() => {
    const stepId = currentStep?.id;
    if (tourIsActive && appMode === 'demo') {
      if (stepId === 'workflow-morning' && !_automationFiredThisTour) {
        _automationFiredThisTour = true;
        setTimeout(() => handleRunAutomation(), 1500);
      } else if (stepId === 'claim-robot-header') {
        // Re-trigger for feature tour — reset and force run
        setAutomationComplete(false);
        setRunningAutomation(false);
        setStreamItems([]);
        _persistedAutomationComplete = false;
        _persistedStreamItems = [];
        setTimeout(() => handleRunAutomation(true), 2000);
      }
    }
    if (!tourIsActive) {
      _automationFiredThisTour = false;
      _persistedStreamItems = [];
      _persistedAutomationComplete = false;
    }
  }, [currentStep?.id, tourIsActive, appMode]);

  const handleRunAutomation = async (force = false) => {
    if (!force && (runningAutomation || automationComplete)) return;
    setRunningAutomation(true);
    setAutomationComplete(false);
    setStreamItems([]);

    if (appMode === 'demo') {
      // Play through the demo stream with delays — append so messages read top-to-bottom
      let accumulated = 0;
      for (const event of demoAutomationStream) {
        accumulated += event.delay;
        setTimeout(() => {
          setStreamItems(prev => [...prev, { ...event, created_at: new Date().toISOString() }]);
        }, accumulated);
      }
      setTimeout(() => {
        setRunningAutomation(false);
        setAutomationComplete(true);
        setAutomationActivity(prev => ({
          ...prev,
          todaySummary: {
            auto_appeal_generated: 3,
            claim_aging_flagged: 2,
            claim_scrubbed: 7,
            payment_reminder_sent: 4,
          }
        }));
      }, accumulated + 200);
    } else {
      // Production: call real API
      try {
        const result = await awsApi.runAutomation();
        if (result.success) {
          const fresh = await awsApi.getAutomationActivity();
          if (fresh.success) {
            // Merge todaySummary — keep the higher value so existing counts aren't wiped
            setAutomationActivity(prev => {
              const prevSummary = prev?.todaySummary || {};
              const freshSummary = fresh.data?.todaySummary || {};
              const allKeys = new Set([...Object.keys(prevSummary), ...Object.keys(freshSummary)]);
              const merged = {};
              allKeys.forEach(k => { merged[k] = Math.max(prevSummary[k] || 0, freshSummary[k] || 0); });
              return { ...fresh.data, todaySummary: merged };
            });
          }
        }
      } catch (err) {
        console.error('Automation run error:', err);
      } finally {
        setRunningAutomation(false);
      }
    }
  };

  return (
    <div>
      {showOnboarding && <OnboardingWizard onClose={() => setShowOnboarding(false)} />}

      {showActivityLog && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowActivityLog(false)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[80vh] flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
              <div>
                <h2 className="text-lg font-bold text-slate-800">Full Automation Log</h2>
                <p className="text-xs text-slate-500 mt-0.5">{activityTotal} total events</p>
              </div>
              <button onClick={() => setShowActivityLog(false)} className="text-slate-400 hover:text-slate-600 text-xl font-bold">×</button>
            </div>
            <div className="overflow-y-auto flex-1">
              {activityLoading ? (
                <div className="flex items-center justify-center py-16 text-slate-400 text-sm">Loading...</div>
              ) : allActivity.length === 0 ? (
                <div className="flex items-center justify-center py-16 text-slate-400 text-sm">No activity found</div>
              ) : (
                <div className="divide-y divide-slate-100">
                  {allActivity.map((item, i) => {
                    const labels = {
                      auto_appeal_generated: { label: 'Appeal Auto-Filed', color: 'bg-teal-100 text-teal-700', dot: 'bg-teal-400' },
                      claim_aging_flagged: { label: 'Aging Claim Flagged', color: 'bg-amber-100 text-amber-700', dot: 'bg-amber-400' },
                      claim_scrubbed: { label: 'Claim Scrubbed', color: 'bg-blue-100 text-blue-700', dot: 'bg-blue-400' },
                      auto_fax_sent: { label: 'Fax Sent', color: 'bg-purple-100 text-purple-700', dot: 'bg-purple-400' },
                      appeal_faxed: { label: 'Appeal Faxed', color: 'bg-purple-100 text-purple-700', dot: 'bg-purple-400' },
                      payment_reminder_sent: { label: 'Payment Reminder Sent', color: 'bg-green-100 text-green-700', dot: 'bg-green-400' },
                      payment_link_sent: { label: 'ACH Payment Link Sent', color: 'bg-emerald-100 text-emerald-700', dot: 'bg-emerald-400' },
                      era_posted: { label: 'ERA Auto-Posted', color: 'bg-green-100 text-green-700', dot: 'bg-green-400' },
                      secondary_claim_created: { label: 'Secondary Claim Created', color: 'bg-indigo-100 text-indigo-700', dot: 'bg-indigo-400' },
                      preauth_auto_created: { label: 'Pre-Auth Created', color: 'bg-indigo-100 text-indigo-700', dot: 'bg-indigo-400' },
                    };
                    const meta = labels[item.action] || { label: item.action, color: 'bg-slate-100 text-slate-700', dot: 'bg-slate-400' };
                    const details = item.metadata ? (typeof item.metadata === 'string' ? JSON.parse(item.metadata) : item.metadata) : {};
                    return (
                      <div key={i} className="px-6 py-3.5 flex items-center justify-between hover:bg-slate-50 transition-colors">
                        <div className="flex items-center gap-3">
                          <span className={`w-2 h-2 rounded-full flex-shrink-0 ${meta.dot}`} />
                          <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${meta.color}`}>{meta.label}</span>
                          <span className="text-sm text-slate-600">
                            {details.patient || details.patient_name || (details.claim_id ? 'Claim #' + details.claim_id?.slice(0, 8) : '')}
                            {details.payer ? ` · ${details.payer}` : ''}
                            {details.amount ? ` · $${parseFloat(details.amount).toLocaleString()}` : ''}
                            {details.days_old ? ` · ${details.days_old} days old` : ''}
                            {details.priority ? ` · ${details.priority}` : ''}
                          </span>
                        </div>
                        <span className="text-xs text-slate-400 flex-shrink-0">{new Date(item.created_at).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            {activityTotal > 25 && (
              <div className="px-6 py-3 border-t border-slate-100 flex items-center justify-between bg-slate-50">
                <span className="text-xs text-slate-500">Page {activityPage} of {Math.ceil(activityTotal / 25)}</span>
                <div className="flex gap-2">
                  <button disabled={activityPage === 1} onClick={() => handleViewAllActivity(activityPage - 1)}
                    className="text-xs px-3 py-1.5 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed">← Prev</button>
                  <button disabled={activityPage >= Math.ceil(activityTotal / 25)} onClick={() => handleViewAllActivity(activityPage + 1)}
                    className="text-xs px-3 py-1.5 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed">Next →</button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}


      <main className="px-6 py-8">
        {activeTab === 'dashboard' && (
          <div className="space-y-6">
            {/* Show skeleton while loading in production mode */}
            {dashboardLoading ? (
              <>
                <SkeletonChart height="h-64" />
                <SkeletonChart height="h-48" />
              </>
            ) : (
            <>
            {/* Claim Robot — Hero Section */}
            {automationActivity && (
              <div data-tour="claim-robot-header" className="rounded-2xl overflow-hidden shadow-lg border border-teal-900/20">
                {/* Gradient Header */}
                <div style={{ background: 'linear-gradient(135deg, #0f4c4c 0%, #134e4a 50%, #0d3d3d 100%)' }} className="px-8 py-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="relative">
                        <div className="w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center">
                          <Bot className="w-6 h-6 text-teal-300" />
                        </div>
                        <span className="absolute -top-1 -right-1 flex h-3 w-3">
                          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-teal-400 opacity-75"></span>
                          <span className="relative inline-flex rounded-full h-3 w-3 bg-teal-300"></span>
                        </span>
                      </div>
                      <div>
                        <div className="flex items-center gap-3">
                          <h2 className="text-2xl font-bold text-white tracking-tight">Claim Robot</h2>
                        </div>
                        <p className="text-teal-300 text-sm mt-0.5">AI-powered automation working 24/7 — appeals, scrubbing, aging alerts, payment reminders</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <button
                        onClick={() => navigate('/settings')}
                        className="text-xs text-teal-300 hover:text-white font-medium border border-teal-700 hover:border-teal-500 px-3 py-2 rounded-lg transition-colors"
                      >
                        Configure
                      </button>
                      <button
                        data-tour="claim-robot-run"
                        onClick={handleRunAutomation}
                        disabled={runningAutomation || automationComplete}
                        className={`flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-bold transition-all shadow-lg ${
                          runningAutomation
                            ? 'bg-teal-700/50 text-teal-300 cursor-not-allowed'
                            : automationComplete
                            ? 'bg-green-600/40 text-green-300 cursor-default'
                            : 'bg-teal-400 hover:bg-teal-300 text-teal-950 hover:shadow-teal-400/30 hover:shadow-xl'
                        }`}
                      >
                        <Zap className={`w-4 h-4 ${runningAutomation ? 'animate-pulse' : ''}`} />
                        {runningAutomation ? 'Running...' : automationComplete ? 'Completed ✓' : 'Run Now'}
                      </button>
                    </div>
                  </div>

                  {/* Stats Row */}
                  <div data-tour="claim-robot-stats" className="grid grid-cols-4 gap-4 mt-6">
                    {[
                      { label: 'Appeals Auto-Filed', value: automationActivity.todaySummary?.auto_appeal_generated || 0, accent: '#2dd4bf', iconBg: 'rgba(45,212,191,0.15)', icon: Shield },
                      { label: 'Aging Claims Flagged', value: automationActivity.todaySummary?.claim_aging_flagged || 0, accent: '#fbbf24', iconBg: 'rgba(251,191,36,0.15)', icon: Clock },
                      { label: 'Claims Scrubbed', value: automationActivity.todaySummary?.claim_scrubbed || 0, accent: '#60a5fa', iconBg: 'rgba(96,165,250,0.15)', icon: CheckCircle },
                      { label: 'Payment Reminders', value: automationActivity.todaySummary?.payment_reminder_sent || 0, accent: '#4ade80', iconBg: 'rgba(74,222,128,0.15)', icon: TrendingUp },
                    ].map(({ label, value, accent, iconBg, icon: Icon }) => (
                      <div key={label} style={{ background: 'rgba(255,255,255,0.13)', border: '1px solid rgba(255,255,255,0.25)', borderRadius: 16, padding: '18px 20px', backdropFilter: 'blur(8px)' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
                          <div style={{ width: 36, height: 36, borderRadius: 10, background: iconBg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                            <Icon style={{ width: 18, height: 18, color: accent }} />
                          </div>
                          <span style={{ fontSize: 11, fontWeight: 700, color: 'rgba(255,255,255,0.85)', textTransform: 'uppercase', letterSpacing: '0.06em', lineHeight: 1.3 }}>{label}</span>
                        </div>
                        <p style={{ fontSize: 44, fontWeight: 900, color: '#fff', lineHeight: 1, margin: 0 }}>{value}</p>
                        <p style={{ fontSize: 11, color: 'rgba(255,255,255,0.55)', marginTop: 6 }}>today</p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Live Terminal — shows when Run Now is active */}
                {streamItems.length > 0 && (
                  <div className="bg-slate-950 px-5 py-3 space-y-1.5 font-mono text-xs border-b border-slate-800">
                    <div className="flex items-center gap-2 mb-2 pb-2 border-b border-slate-800">
                      <span className="w-2.5 h-2.5 rounded-full bg-red-500" />
                      <span className="w-2.5 h-2.5 rounded-full bg-yellow-500" />
                      <span className="w-2.5 h-2.5 rounded-full bg-green-500" />
                      <span className="text-slate-500 ml-2">claim-robot — live output</span>
                    </div>
                    {streamItems.map((item, i) => (
                      <div key={i} className={`flex items-start gap-2 ${
                        item.action === 'complete' ? 'text-green-400' :
                        item.action === 'auto_appeal_generated' ? 'text-teal-400' :
                        item.action === 'claim_aging_flagged' ? 'text-amber-400' :
                        item.action === 'claim_scrubbed' ? 'text-blue-400' :
                        item.action === 'payment_reminder_sent' ? 'text-green-300' :
                        item.action === 'payment_link_sent' ? 'text-emerald-400' :
                        item.action === 'appeal_faxed' ? 'text-purple-400' :
                        item.action === 'secondary_claim_created' ? 'text-indigo-400' :
                        item.action === 'era_posted' ? 'text-blue-400' : 'text-slate-300'
                      }`}>
                        <span className="text-slate-600 flex-shrink-0">{new Date(item.created_at).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}</span>
                        <span className="text-slate-500 flex-shrink-0">›</span>
                        <span>{item.message}</span>
                      </div>
                    ))}
                    {runningAutomation && (
                      <div className="flex items-center gap-1 text-teal-400 mt-1">
                        <span className="inline-block w-1.5 h-3.5 bg-teal-400 animate-pulse rounded-sm" />
                      </div>
                    )}
                  </div>
                )}

                {/* Activity Log */}
                <div data-tour="claim-robot-stream" className="bg-white">
                  {automationActivity.activity?.length > 0 ? (
                    <div className="divide-y divide-slate-100">
                      <div className="px-6 py-3 bg-slate-50 border-b border-slate-100">
                        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Recent Activity</p>
                      </div>
                      {automationActivity.activity.slice(0, 8).map((item, i) => {
                        const labels = {
                          auto_appeal_generated: { label: 'Appeal Auto-Filed', color: 'bg-teal-100 text-teal-700', dot: 'bg-teal-400' },
                          claim_aging_flagged: { label: 'Aging Claim Flagged', color: 'bg-amber-100 text-amber-700', dot: 'bg-amber-400' },
                          claim_scrubbed: { label: 'Claim Scrubbed', color: 'bg-blue-100 text-blue-700', dot: 'bg-blue-400' },
                          auto_fax_sent: { label: 'Fax Sent', color: 'bg-purple-100 text-purple-700', dot: 'bg-purple-400' },
                          appeal_faxed: { label: 'Appeal Faxed', color: 'bg-purple-100 text-purple-700', dot: 'bg-purple-400' },
                          payment_reminder_sent: { label: 'Payment Reminder Sent', color: 'bg-green-100 text-green-700', dot: 'bg-green-400' },
                          payment_link_sent: { label: 'ACH Payment Link Sent', color: 'bg-emerald-100 text-emerald-700', dot: 'bg-emerald-400' },
                          era_posted: { label: 'ERA Auto-Posted', color: 'bg-green-100 text-green-700', dot: 'bg-green-400' },
                          secondary_claim_created: { label: 'Secondary Claim Created', color: 'bg-indigo-100 text-indigo-700', dot: 'bg-indigo-400' },
                        };
                        const meta = labels[item.action] || { label: item.action, color: 'bg-slate-100 text-slate-700', dot: 'bg-slate-400' };
                        const details = item.metadata ? (typeof item.metadata === 'string' ? JSON.parse(item.metadata) : item.metadata) : {};
                        return (
                          <div key={i} className="px-6 py-3.5 flex items-center justify-between hover:bg-slate-50 transition-colors">
                            <div className="flex items-center gap-3">
                              <span className={`w-2 h-2 rounded-full flex-shrink-0 ${meta.dot}`} />
                              <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${meta.color}`}>{meta.label}</span>
                              <span className="text-sm text-slate-600">
                                {details.patient || details.patient_name || (details.claim_id ? 'Claim #' + details.claim_id?.slice(0, 8) : '')}
                                {details.payer ? ` · ${details.payer}` : ''}
                                {details.amount ? ` · $${parseFloat(details.amount).toLocaleString()}` : ''}
                                {details.days_old ? ` · ${details.days_old} days old` : ''}
                                {details.priority ? ` · ${details.priority}` : ''}
                                {details.risk_level ? ` · ${details.risk_level} risk` : ''}
                              </span>
                            </div>
                            <span className="text-xs text-slate-400 flex-shrink-0">{new Date(item.created_at).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}</span>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="px-6 py-12 text-center text-slate-500 text-sm">
                      <div className="w-12 h-12 rounded-full bg-teal-50 flex items-center justify-center mx-auto mb-3">
                        <Zap className="w-6 h-6 text-teal-300" />
                      </div>
                      <p className="font-semibold text-slate-700">No automation activity yet</p>
                      <p className="text-xs mt-1 text-slate-400">FlowRCM runs automatically every night. Press <strong>Run Now</strong> to trigger it immediately.</p>
                    </div>
                  )}
                  {automationActivity.activity?.length > 0 && (
                    <div className="px-6 py-3 border-t border-slate-100 bg-slate-50">
                      <button
                        onClick={() => handleViewAllActivity(1)}
                        className="text-xs font-semibold text-teal-600 hover:text-teal-700 transition-colors"
                      >
                        View all activity →
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Denied Claims Queue */}
            {deniedClaims.length > 0 && (
              <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
                <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-red-50 rounded-lg">
                      <AlertTriangle className="w-5 h-5 text-red-600" />
                    </div>
                    <div>
                      <h2 className="text-lg font-bold text-slate-900">Denied Claims — Action Required</h2>
                      <p className="text-sm text-slate-500">{deniedClaims.length} claim{deniedClaims.length !== 1 ? 's' : ''} need attention · Use AI Denial Recovery to appeal</p>
                    </div>
                  </div>
                </div>
                <div className="divide-y divide-slate-100">
                  {deniedClaims.slice(0, 8).map(claim => (
                    <div key={claim.id} className="px-6 py-4 flex items-center justify-between hover:bg-slate-50 transition-colors">
                      <div className="flex items-center gap-4">
                        <div>
                          <p className="font-medium text-slate-900">{claim.patient_name || 'Unknown Patient'}</p>
                          <div className="flex items-center gap-2 mt-1">
                            <span className="text-sm text-slate-500">{claim.procedure_codes}</span>
                            <span className="text-slate-300">•</span>
                            <span className="text-sm text-slate-500">{claim.payer_name}</span>
                            {claim.denial_code && (
                              <>
                                <span className="text-slate-300">•</span>
                                <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded font-medium">{claim.denial_code}</span>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <p className="font-semibold text-slate-900">${parseFloat(claim.claim_amount || 0).toLocaleString()}</p>
                          <p className="text-xs text-slate-500">{claim.service_date ? new Date(claim.service_date).toLocaleDateString('en-US', { timeZone: 'UTC' }) : ''}</p>
                        </div>
                        <button
                          onClick={() => navigate('/claims')}
                          className="flex items-center gap-2 px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white text-sm font-semibold rounded-lg transition-colors"
                        >
                          <Zap className="w-4 h-4" />
                          Run AI Recovery
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
                {deniedClaims.length > 8 && (
                  <div className="px-6 py-3 bg-slate-50 border-t border-slate-200 text-center">
                    <span className="text-sm text-slate-500">Showing 8 of {deniedClaims.length} denied claims</span>
                  </div>
                )}
              </div>
            )}

            {/* Claim Aging Queue */}
            {agingData && (agingData.buckets?.length > 0 || agingData.urgentClaims?.length > 0) && (
              <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
                <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-amber-50 rounded-lg">
                      <Clock className="w-5 h-5 text-amber-600" />
                    </div>
                    <div>
                      <h2 className="text-lg font-bold text-slate-900">Claim Aging</h2>
                      <p className="text-sm text-slate-500">Outstanding claims by age — older = higher denial risk</p>
                    </div>
                  </div>
                </div>
                {/* Aging Buckets */}
                {agingData.buckets?.length > 0 && (
                  <div className="grid grid-cols-4 divide-x divide-slate-100 border-b border-slate-100">
                    {[
                      { label: '0–30 Days', key: '0-30', color: 'text-green-600', bg: 'bg-green-50' },
                      { label: '31–60 Days', key: '31-60', color: 'text-yellow-600', bg: 'bg-yellow-50' },
                      { label: '61–90 Days', key: '61-90', color: 'text-orange-600', bg: 'bg-orange-50' },
                      { label: '90+ Days', key: '90+', color: 'text-red-600', bg: 'bg-red-50' },
                    ].map(({ label, key, color, bg }) => {
                      const bucket = agingData.buckets?.find(b => b.bucket === key);
                      return (
                        <div key={key} className={`p-4 text-center ${bg}`}>
                          <p className="text-xs text-slate-500 mb-1">{label}</p>
                          <p className={`text-2xl font-bold ${color}`}>{bucket?.count || 0}</p>
                          <p className="text-xs text-slate-500">${parseFloat(bucket?.outstanding_amount || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })} outstanding</p>
                        </div>
                      );
                    })}
                  </div>
                )}
                {/* Urgent Claims */}
                {agingData.urgentClaims?.length > 0 && (
                  <div className="divide-y divide-slate-100">
                    {agingData.urgentClaims.slice(0, 5).map(claim => (
                      <div key={claim.id} className="px-6 py-3 flex items-center justify-between hover:bg-slate-50">
                        <div>
                          <p className="font-medium text-slate-900 text-sm">{claim.patient_name || 'Unknown'}</p>
                          <p className="text-xs text-slate-500">{claim.payer} · {Array.isArray(claim.procedure_codes) ? claim.procedure_codes.join(', ') : claim.procedure_codes}</p>
                        </div>
                        <div className="flex items-center gap-4">
                          <div className="text-right">
                            <p className="font-semibold text-slate-900 text-sm">${parseFloat(claim.claim_amount || 0).toLocaleString()}</p>
                            <span className={`text-xs font-medium px-2 py-0.5 rounded ${claim.days_old > 90 ? 'bg-red-100 text-red-700' : claim.days_old > 60 ? 'bg-orange-100 text-orange-700' : 'bg-yellow-100 text-yellow-700'}`}>
                              {claim.days_old}d old
                            </span>
                          </div>
                          <button
                            onClick={() => navigate('/claims')}
                            className="text-xs px-3 py-1.5 bg-teal-600 hover:bg-teal-700 text-white rounded-lg font-medium transition-colors"
                          >
                            Follow Up
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            </>
            )}
          </div>
        )}

        {activeTab === 'reports' && (
  <div className="space-y-6">
    <div className="bg-white rounded-xl shadow-sm p-6 border border-slate-200">
      <h2 className="text-xl font-bold text-slate-900 mb-6 flex items-center gap-2">
        <FileText className="w-5 h-5 text-teal-600" />
        Available Reports
      </h2>

      {reportMessage && (
        <div style={{
          backgroundColor: reportMessage.type === 'success' ? '#f0fdf4' : '#fef2f2',
          border: `1px solid ${reportMessage.type === 'success' ? '#bbf7d0' : '#fecaca'}`,
          borderRadius: '8px',
          padding: '12px 16px',
          marginBottom: '20px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          {reportMessage.type === 'success' ? (
            <CheckCircle size={18} style={{ color: '#16a34a' }} />
          ) : (
            <AlertCircle size={18} style={{ color: '#dc2626' }} />
          )}
          <span style={{ color: reportMessage.type === 'success' ? '#166534' : '#991b1b', fontSize: '14px' }}>
            {reportMessage.text}
          </span>
        </div>
      )}

      <div className="grid grid-cols-2 gap-4">
        {['AR Aging Detail', 'Denial Analysis', 'Payer Performance Summary', 'Production Report', 'Collection Report', 'Workflow Activity'].map((report, idx) => (
          <button 
            key={idx} 
            onClick={() => handleReportDownload(report)}
            disabled={reportLoading === report}
            className="flex items-center justify-between p-4 rounded-lg border-2 border-slate-200 hover:border-teal-500 hover:shadow-md transition-all group disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <div className="flex items-center gap-3">
              <FileText className="w-5 h-5 text-teal-600 group-hover:text-teal-700" />
              <span className="font-semibold text-slate-900">{report}</span>
            </div>
            {reportLoading === report ? (
              <div className="w-5 h-5 border-2 border-teal-600 border-t-transparent rounded-full animate-spin"></div>
            ) : (
              <Download className="w-5 h-5 text-slate-400 group-hover:text-teal-600" />
            )}
          </button>
        ))}
      </div>
      <div className="mt-6 p-4 bg-teal-50 rounded-lg border border-teal-200">
        <div className="flex items-center gap-2 text-teal-700">
          <AlertCircle className="w-5 h-5" />
          <span className="text-sm"><strong>Custom report generation coming soon.</strong> Export standardized reports above, or contact support for custom analytics needs.</span>
        </div>
      </div>
    </div>
  </div>
)}
         
      </main>

    </div>
  );
};
export default App;